import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import './AcceptInvitation.css';

function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isLoggedIn = !!localStorage.getItem('access_token');

  useEffect(() => {
    fetchInvitationInfo();
  }, [token]);

  const fetchInvitationInfo = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`myclub/admins/invitation/${token}/`);
      setInvitation(response.data);

      if (response.data.status !== 'pending') {
        setError(`This invitation has already been ${response.data.status}.`);
      } else if (response.data.is_expired) {
        setError('This invitation has expired. Please request a new one from the club owner.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired invitation.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isLoggedIn) {
      // Store the invitation token and redirect to login
      localStorage.setItem('pending_invitation_token', token);
      navigate('/myclub/login?redirect=accept-invitation');
      return;
    }

    setAccepting(true);
    setError('');

    try {
      const response = await axiosInstance.post('myclub/admins/accept-invitation/', { token });

      if (response.data.requires_auth) {
        // User needs to log in first
        localStorage.setItem('pending_invitation_token', token);
        navigate('/myclub/login?redirect=accept-invitation');
        return;
      }

      setSuccess(`You are now an admin of ${response.data.club_name}!`);

      // Redirect to club dashboard after a short delay
      setTimeout(() => {
        navigate('/myclub/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleRegisterRedirect = () => {
    // Redirect to the dedicated admin registration page with the token
    navigate(`/club-admin/register/${token}`);
  };

  if (loading) {
    return (
      <div className="accept-invitation-page">
        <div className="accept-invitation-card">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="accept-invitation-page">
        <div className="accept-invitation-card">
          <div className="error-state">
            <h2>Invalid Invitation</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invitation-page">
      <div className="accept-invitation-card">
        <div className="invitation-header">
          <h1>Club Admin Invitation</h1>
        </div>

        <div className="invitation-content">
          {success ? (
            <div className="success-state">
              <div className="success-icon">✓</div>
              <h2>Welcome!</h2>
              <p>{success}</p>
              <p className="redirect-text">Redirecting to dashboard...</p>
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : (
            <>
              <div className="club-info">
                <h2>{invitation?.club_name}</h2>
                <p className="club-location">
                  {invitation?.club_city}, {invitation?.club_state}
                </p>
              </div>

              <p className="invitation-text">
                <strong>{invitation?.invited_by}</strong> has invited you to become an admin of this club on privateIQ.
              </p>

              <p className="invitation-email">
                Invitation sent to: <strong>{invitation?.email}</strong>
              </p>

              {isLoggedIn ? (
                <button
                  className="btn-primary accept-btn"
                  onClick={handleAccept}
                  disabled={accepting}
                >
                  {accepting ? 'Accepting...' : 'Accept Invitation'}
                </button>
              ) : (
                <div className="auth-options">
                  <p className="auth-prompt">Create an account to accept this invitation and start managing your club.</p>
                  <button className="btn-primary" onClick={handleRegisterRedirect}>
                    Create Account
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitation;
